import { type FC, type KeyboardEvent, useCallback, useId, useRef, useState } from 'react';

import { PhotoIcon } from '@heroicons/react/24/outline';
import type { Editor } from '@tiptap/react';
import cn from 'classnames';

import type { CommunityPostComposerProps } from '../../CommunityPostComposer.type';
import { ICON_CLASS, tr } from '../constants';
import { MediaGifTab } from './MediaGifTab';
import { MediaUploadTab } from './MediaUploadTab';
import { PanelPopover } from './PanelPopover';

type MediaTab = 'gif' | 'upload';

interface MediaPanelProps {
  editor: Editor;
  translations?: Record<string, string>;
  onUploadImage?: CommunityPostComposerProps['onUploadImage'];
  isMediaUploadDisabled?: CommunityPostComposerProps['isMediaUploadDisabled'];
  uploadAccept?: string;
  uploadMaxBytes?: number;
  onSearchGifs?: CommunityPostComposerProps['onSearchGifs'];
  gifSearchLimit?: number;
  gifSearchDebounceMs?: number;
}

export const MediaPanel: FC<MediaPanelProps> = ({
  editor,
  translations,
  onUploadImage,
  isMediaUploadDisabled,
  uploadAccept,
  uploadMaxBytes,
  onSearchGifs,
  gifSearchLimit,
  gifSearchDebounceMs,
}) => {
  const hasGifSearch = Boolean(onSearchGifs);
  const hasUpload = Boolean(onUploadImage);
  const initialTab: MediaTab = hasGifSearch ? 'gif' : 'upload';

  const [tab, setTab] = useState<MediaTab>(initialTab);
  const baseId = useId();
  const gifTabId = `${baseId}-tab-gif`;
  const uploadTabId = `${baseId}-tab-upload`;
  const gifPanelId = `${baseId}-panel-gif`;
  const uploadPanelId = `${baseId}-panel-upload`;
  const showTabs = hasGifSearch && hasUpload;
  const gifTabRef = useRef<HTMLButtonElement | null>(null);
  const uploadTabRef = useRef<HTMLButtonElement | null>(null);

  const activateTab = useCallback((next: MediaTab) => {
    setTab(next);
    (next === 'gif' ? gifTabRef : uploadTabRef).current?.focus();
  }, []);

  const handleTabKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowLeft':
          event.preventDefault();
          activateTab(tab === 'gif' ? 'upload' : 'gif');
          break;
        case 'Home':
          event.preventDefault();
          activateTab('gif');
          break;
        case 'End':
          event.preventDefault();
          activateTab('upload');
          break;
      }
    },
    [activateTab, tab],
  );

  if (!hasGifSearch && !hasUpload) return null;

  return (
    <PanelPopover
      triggerLabel={tr(translations, 'community.composer.add_media')}
      triggerIcon={<PhotoIcon className={ICON_CLASS} aria-hidden="true" />}
      panelClassName="w-80"
    >
      {({ close }) => (
        <>
          {showTabs && (
            <div
              className="flex gap-1 p-2 border-b border-black/5"
              role="tablist"
              aria-label={tr(translations, 'community.composer.media_panel.tabs')}
            >
              <button
                ref={gifTabRef}
                type="button"
                role="tab"
                id={gifTabId}
                aria-selected={tab === 'gif'}
                aria-controls={gifPanelId}
                tabIndex={tab === 'gif' ? 0 : -1}
                onClick={() => setTab('gif')}
                onKeyDown={handleTabKeyDown}
                className={cn('px-3 py-1 text-sm rounded-full', {
                  'bg-black/10 font-medium': tab === 'gif',
                  'hover:bg-black/5': tab !== 'gif',
                })}
              >
                {tr(translations, 'community.composer.media_tab_gif')}
              </button>
              <button
                ref={uploadTabRef}
                type="button"
                role="tab"
                id={uploadTabId}
                aria-selected={tab === 'upload'}
                aria-controls={uploadPanelId}
                tabIndex={tab === 'upload' ? 0 : -1}
                onClick={() => setTab('upload')}
                onKeyDown={handleTabKeyDown}
                className={cn('px-3 py-1 text-sm rounded-full', {
                  'bg-black/10 font-medium': tab === 'upload',
                  'hover:bg-black/5': tab !== 'upload',
                })}
              >
                {tr(translations, 'community.composer.media_tab_upload')}
              </button>
            </div>
          )}
          {tab === 'gif' &&
            onSearchGifs &&
            (showTabs ? (
              <div role="tabpanel" id={gifPanelId} aria-labelledby={gifTabId}>
                <MediaGifTab
                  editor={editor}
                  translations={translations}
                  onSearchGifs={onSearchGifs}
                  onInserted={close}
                  gifSearchLimit={gifSearchLimit}
                  gifSearchDebounceMs={gifSearchDebounceMs}
                />
              </div>
            ) : (
              <MediaGifTab
                editor={editor}
                translations={translations}
                onSearchGifs={onSearchGifs}
                onInserted={close}
                gifSearchLimit={gifSearchLimit}
                gifSearchDebounceMs={gifSearchDebounceMs}
              />
            ))}
          {tab === 'upload' &&
            onUploadImage &&
            (showTabs ? (
              <div role="tabpanel" id={uploadPanelId} aria-labelledby={uploadTabId}>
                <MediaUploadTab
                  editor={editor}
                  translations={translations}
                  onUploadImage={onUploadImage}
                  isMediaUploadDisabled={isMediaUploadDisabled}
                  onInserted={close}
                  uploadAccept={uploadAccept}
                  uploadMaxBytes={uploadMaxBytes}
                />
              </div>
            ) : (
              <MediaUploadTab
                editor={editor}
                translations={translations}
                onUploadImage={onUploadImage}
                isMediaUploadDisabled={isMediaUploadDisabled}
                onInserted={close}
                uploadAccept={uploadAccept}
                uploadMaxBytes={uploadMaxBytes}
              />
            ))}
        </>
      )}
    </PanelPopover>
  );
};
